import {Moment} from 'moment'

import {Iso8601, IsoWeekDay} from './atomic.types'

export const taskRepetitionUnits = ['daily', 'weekly', 'monthly', 'yearly'] as const
export type TaskRepetitionUnit = (typeof taskRepetitionUnits)[number]

export type QuickSelectRepetition = {
  repetition: TaskRepetitionUnit
  enabled?: boolean
}

type EndingOptionsDto =
  | {endDate: Iso8601; endsAfterRepetitions?: never} // endDate or
  | {endDate?: never; endsAfterRepetitions: number} // endsAfterRepetitions or
  | {endDate?: never; endsAfterRepetitions?: never} // non

type EndingOptions =
  | {endDate: Moment; endsAfterRepetitions?: never} // endDate or
  | {endDate?: never; endsAfterRepetitions: number} // endsAfterRepetitions or
  | {endDate?: never; endsAfterRepetitions?: never} // non

type CustomDailyRepetitionDto = EndingOptionsDto & {
  repetition: 'daily'
  startDate: Iso8601
  repeatEvery: number
  enabled?: boolean
}

export type CustomDailyRepetition = Omit<
  CustomDailyRepetitionDto,
  'startDate' | 'endDate' | 'endsAfterRepetitions'
> &
  EndingOptions & {
    startDate: Moment
  }

type CustomWeeklyRepetitionDto = EndingOptionsDto & {
  repetition: 'weekly'
  startDate: Iso8601
  repeatEvery: number
  weekDays: IsoWeekDay[]
  enabled?: boolean
}

export type CustomWeeklyRepetition = Omit<
  CustomWeeklyRepetitionDto,
  'startDate' | 'endDate' | 'endsAfterRepetitions'
> &
  EndingOptions & {
    startDate: Moment
  }

type CustomMonthlyRepetitionDto = EndingOptionsDto & {
  repetition: 'monthly'
  startDate: Iso8601
  repeatEvery: number
  enabled?: boolean
}

export type CustomMonthlyRepetition = Omit<
  CustomMonthlyRepetitionDto,
  'startDate' | 'endDate' | 'endsAfterRepetitions'
> &
  EndingOptions & {
    startDate: Moment
  }

type CustomYearlyRepetitionDto = EndingOptionsDto & {
  repetition: 'yearly'
  startDate: Iso8601
  repeatEvery: number
  enabled?: boolean
}

export type CustomYearlyRepetition = Omit<
  CustomYearlyRepetitionDto,
  'startDate' | 'endDate' | 'endsAfterRepetitions'
> &
  EndingOptions & {
    startDate: Moment
  }

export type CustomTaskRepetition =
  | CustomDailyRepetition
  | CustomWeeklyRepetition
  | CustomMonthlyRepetition
  | CustomYearlyRepetition

export type TaskRepetitionDto =
  | QuickSelectRepetition
  | CustomDailyRepetitionDto
  | CustomWeeklyRepetitionDto
  | CustomMonthlyRepetitionDto
  | CustomYearlyRepetitionDto

export type TaskRepetition =
  | QuickSelectRepetition
  | CustomDailyRepetition
  | CustomWeeklyRepetition
  | CustomMonthlyRepetition
  | CustomYearlyRepetition

export function isCustomDailyRepetition(
  repetitionInfo: TaskRepetition
): repetitionInfo is CustomDailyRepetition {
  return repetitionInfo.repetition === 'daily' && 'repeatEvery' in repetitionInfo
}

export function isCustomWeeklyRepetition(
  repetitionInfo: TaskRepetition
): repetitionInfo is CustomWeeklyRepetition {
  return (
    repetitionInfo.repetition === 'weekly' &&
    'repeatEvery' in repetitionInfo &&
    'weekDays' in repetitionInfo
  )
}

export function isCustomMonthlyRepetition(
  repetitionInfo: TaskRepetition
): repetitionInfo is CustomMonthlyRepetition {
  return repetitionInfo.repetition === 'monthly' && 'repeatEvery' in repetitionInfo
}

export function isCustomYearlyRepetition(
  repetitionInfo: TaskRepetition
): repetitionInfo is CustomYearlyRepetition {
  return repetitionInfo.repetition === 'yearly' && 'repeatEvery' in repetitionInfo
}

/**
 * currently there is no solid way of determine that it is a custom setting
 * other then checking for custom parameters
 */
export function isCustomRepetition(
  repetitionInfo: TaskRepetition
): repetitionInfo is CustomTaskRepetition {
  return (
    isCustomDailyRepetition(repetitionInfo) ||
    isCustomWeeklyRepetition(repetitionInfo) ||
    isCustomMonthlyRepetition(repetitionInfo) ||
    isCustomYearlyRepetition(repetitionInfo) ||
    'endDate' in repetitionInfo ||
    'endsAfterRepetitions' in repetitionInfo
  )
}
